"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { api } from "@/lib/api";
import { useInternalChatSocket } from "./use-internal-chat-socket";

interface InternalUnreadValue {
  total: number;
}

const InternalUnreadContext = createContext<InternalUnreadValue>({ total: 0 });

/** Total de mensagens não-lidas do Chat Interno do usuário logado (app-wide). */
export function useInternalUnread(): InternalUnreadValue {
  return useContext(InternalUnreadContext);
}

/**
 * Mantém, app-wide, o total de não-lidas do Chat Interno pro badge da sidebar
 * — em tempo real, sem o usuário precisar abrir o chat interno.
 *
 * #122 (regra de performance #1): o evento `internal:message:created` incrementa
 * o total **in-place** (sem refetch por evento — antes cada mensagem em um canal
 * de N membros disparava N `GET /internal-chat/unread-counts`). O `GET` fica só
 * pra reconciliar: boot, reconnect, focus/visibility e mark-read. Conta apenas
 * mensagens de OUTROS (a própria, ex. de outra aba, não conta) e deduplica por
 * `message.id` (o mesmo evento pode chegar 2x: bridge + webhook).
 *
 * Reusa `useInternalChatSocket` (auth/reconexão já resolvidos); ao conectar entra
 * nos canais do usuário pra receber o evento mesmo fora da página do chat interno.
 *
 * NOTA: com a página do chat interno aberta há 2 conexões ao mesmo namespace
 * (este provider, montado no layout, + a página). O canal aberto+visível é marcado
 * como lido pela página, que dispara `internal-unread:refresh` → este provider
 * reconcilia o total; o incremento in-place de uma msg do canal aberto é corrigido
 * nesse refresh (janela curta). Para canais fechados, o in-place é a fonte direta.
 */
export function InternalUnreadProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [total, setTotal] = useState(0);
  const currentUserIdRef = useRef<string>("");
  // Dedup por message.id: o mesmo evento pode chegar 2x (bridge + webhook).
  const seenIdsRef = useRef<Set<string>>(new Set());

  const refetch = useCallback(() => {
    api
      .request<{ counts: Record<string, number>; total: number }>(
        "/internal-chat/unread-counts",
      )
      .then((d) => setTotal(d?.total ?? 0))
      .catch(() => {
        /* sem permissão / offline → mantém valor atual, sem badge fantasma */
      });
  }, []);

  // #122: incrementa o total IN-PLACE a partir do payload, sem bater no back.
  const onMessageCreated = useCallback(
    (data: { channelId: string; message: { id?: string; senderId?: string } }) => {
      const msg = data?.message;
      if (!msg || msg.senderId === currentUserIdRef.current) return; // não conta as minhas
      const id = msg.id;
      if (id != null) {
        if (seenIdsRef.current.has(id)) return; // já contada (evento duplicado)
        seenIdsRef.current.add(id);
        if (seenIdsRef.current.size > 500) {
          // mantém só os ids recentes (Set preserva ordem de inserção)
          seenIdsRef.current = new Set([...seenIdsRef.current].slice(-250));
        }
      }
      setTotal((prev) => prev + 1);
    },
    [],
  );

  const { connected, joinChannels } = useInternalChatSocket({ onMessageCreated });

  // Fetch inicial + captura a identidade local (pra filtrar as próprias mensagens).
  useEffect(() => {
    currentUserIdRef.current = api.getUser()?.id ?? "";
    refetch();
  }, [refetch]);

  // Ao (re)conectar, entra nas salas de todos os canais do usuário (pra receber o
  // evento mesmo com a página fechada) E reconcilia o total — mensagens perdidas no
  // downtime do socket não são incrementadas in-place, então o GET no reconnect cura.
  useEffect(() => {
    if (!connected) return;
    let cancelled = false;
    refetch();
    api
      .request<Array<{ id: string }>>("/internal-chat/channels")
      .then((channels) => {
        if (cancelled || !Array.isArray(channels)) return;
        joinChannels(channels.map((c) => c.id));
      })
      .catch((e) => {
        // Sem as salas, o badge para de incrementar in-place (o reconcile por
        // focus/reconnect ainda cura o total) — logamos pra "real-time parou"
        // não ficar invisível, em vez de engolir o erro.
        console.warn("[internal-unread] falha ao entrar nos canais", e);
      });
    return () => {
      cancelled = true;
    };
  }, [connected, joinChannels, refetch]);

  // Re-fetch ao voltar pra aba e quando a página marca lido (badge baixa na hora).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") refetch();
    };
    window.addEventListener("focus", refetch);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("internal-unread:refresh", refetch as EventListener);
    return () => {
      window.removeEventListener("focus", refetch);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(
        "internal-unread:refresh",
        refetch as EventListener,
      );
    };
  }, [refetch]);

  return (
    <InternalUnreadContext.Provider value={{ total }}>
      {children}
    </InternalUnreadContext.Provider>
  );
}
